import React from 'react';
import { motion } from 'framer-motion';
import { CustomerLogoCarousel } from '../components/CustomerLogoCarousel';
import { StrategicVision } from '../components/StrategicVision';
import { Award } from 'lucide-react';

export const AboutUs = () => {
  return (
    <div className="pt-20">
      {/* Page Header */}
      <section className="relative py-20 bg-gradient-to-br from-blue-600 via-blue-700 to-indigo-800 text-white overflow-hidden">
        {/* Decorative Background */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 w-72 h-72 bg-white rounded-full filter blur-3xl"></div>
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-300 rounded-full filter blur-3xl"></div>
        </div>
        
        {/* Animated Shapes */}
        <div className="absolute inset-0">
          <motion.div
            animate={{ rotate: 360 }}
            transition={{ duration: 50, repeat: Infinity, ease: "linear" }}
            className="absolute top-20 right-20 w-32 h-32 border-4 border-white/20 rounded-full"
          />
          <motion.div
            animate={{ rotate: -360 }}
            transition={{ duration: 40, repeat: Infinity, ease: "linear" }}
            className="absolute bottom-20 left-20 w-24 h-24 border-4 border-white/20"
          />
        </div>

        <div className="container mx-auto px-4 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center space-y-6"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ delay: 0.2, type: "spring" }}
              className="inline-flex items-center justify-center w-20 h-20 bg-white/10 backdrop-blur-lg rounded-2xl mb-4"
            >
              <Award className="h-10 w-10 text-white" />
            </motion.div>
            <h1 className="text-5xl lg:text-6xl font-bold">
              About ISDE Academy
            </h1>
            <p className="text-xl text-blue-100 max-w-3xl mx-auto leading-relaxed">
              Building excellence through education since 2015
            </p>
            <div className="flex flex-wrap gap-6 justify-center items-center pt-4">
              <div className="text-center">
                <div className="text-4xl font-bold">10+</div>
                <div className="text-blue-200 text-sm">Years Experience</div>
              </div>
              <div className="w-px h-12 bg-white/30"></div>
              <div className="text-center">
                <div className="text-4xl font-bold">1000+</div>
                <div className="text-blue-200 text-sm">Professionals Trained</div>
              </div>
              <div className="w-px h-12 bg-white/30"></div>
              <div className="text-center">
                <div className="text-4xl font-bold">50+</div>
                <div className="text-blue-200 text-sm">Training Programs</div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto space-y-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold text-gray-900">Our Story</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                ISDE Academy is a leading authority in quality management and professional training, dedicated to empowering businesses across Manufacturing, IT, and Educational sectors. With a proven track record of expertise spanning over 10 years, we offer a wide range of ISO certification courses and professional development programs designed to enhance your knowledge and skills.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                Our commitment to excellence is reflected in our expert instructors, practical training approach, and the success of over 1000 professionals we've trained. We ensure that you're equipped to meet the ever-evolving challenges of your industry with confidence and competence.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      <StrategicVision />

      {/* Our Values Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h2 className="text-3xl font-bold text-gray-900 text-center">Our Values</h2>
              <div className="grid md:grid-cols-3 gap-6">
                {[
                  { title: 'Excellence', description: 'We strive for the highest standards in everything we do' },
                  { title: 'Integrity', description: 'We maintain honesty and transparency in all our interactions' },
                  { title: 'Innovation', description: 'We continuously improve our training methods and content' },
                  { title: 'Practicality', description: 'We focus on real-world application of knowledge' },
                  { title: 'Collaboration', description: 'We work closely with clients to understand their needs' },
                  { title: 'Impact', description: 'We measure success by the impact on professionals\' careers' }
                ].map((value, index) => (
                  <div key={index} className="p-6 border-2 border-blue-100 rounded-xl hover:border-blue-300 transition-colors">
                    <h4 className="text-xl font-bold text-gray-900 mb-2">{value.title}</h4>
                    <p className="text-gray-600">{value.description}</p>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      <CustomerLogoCarousel />
    </div>
  );
};
